import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Flex,
  Badge,
  Divider,
  SimpleGrid,
  Image,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import supabase from '../utils/supabaseUtils';

const UserOrdersHistory = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  useEffect(() => {
    fetchUserOrders();
  }, []);
  
  const fetchUserOrders = async () => {
    try {
      setLoading(true);

      // Get current authenticated user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      /*
        Pull basic order information and embed the order_items array so we can
        derive an item_count without extra round-trips. Only the columns the UI
        needs are selected to keep the payload small.
      */
      const { data, error } = await supabase
        .from('orders')
        .select('id, created_at, status, order_total, package_name, order_items(id)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formatted = (data || []).map((o) => ({
        order_id: o.id,
        created_at: o.created_at,
        status: o.status,
        total: o.order_total,
        item_count: Array.isArray(o.order_items) ? o.order_items.length : 0,
        package: o.package_name || 'Custom'
      }));

      setOrders(formatted);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load your order history');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchOrderDetails = async (orderId) => {
    if (!orderId) return;

    try {
      setDetailsLoading(true);

      /*
        1. Fetch the order record
        2. Fetch order_items linked to that order
        3. For any item that references an export_id, fetch the export so we
           can display its watermarked preview and provide a download URL.
      */
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select('*')
        .eq('id', orderId)
        .single();
      if (orderError) throw orderError;

      const { data: itemsData, error: itemsError } = await supabase
        .from('order_items')
        .select('*')
        .eq('order_id', orderId);
      if (itemsError) throw itemsError;

      // Gather any export_ids so we can bulk-fetch the exports table in one call
      const exportIds = itemsData.filter((it) => it.export_id).map((it) => it.export_id);
      let exportsMap = {};
      if (exportIds.length) {
        const { data: exportsData, error: exportsError } = await supabase
          .from('exports')
          .select('id, export_url, export_url_wm')
          .in('id', exportIds)
          .eq('status', 'active');
        if (exportsError) {
          console.warn('Could not fetch related exports:', exportsError);
        } else {
          exportsMap = Object.fromEntries(exportsData.map((ex) => [ex.id, ex]));
        }
      }

      const enrichedItems = itemsData.map((item) => {
        const exportInfo = exportsMap[item.export_id] || {};
        return {
          ...item,
          watermarked_url: exportInfo.export_url_wm || null,
          download_url: exportInfo.export_url || null
        };
      });

      setOrderDetails({
        order_data: orderData,
        items: enrichedItems
      });
    } catch (err) {
      console.error('Error fetching order details:', err);
      toast({
        title: 'Error',
        description: 'Failed to load order details',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setDetailsLoading(false);
    }
  };
  
  const handleDownload = (item) => {
    // In production, this would update the download count and process the download
    // For now, we'll just show a toast
    toast({
      title: 'Download started',
      description: `Downloading ${item.name}`,
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
    
    // For simulation purposes only - in a real app you would use a real URL
    if (item.download_url && item.download_url !== '#') {
      window.open(item.download_url, '_blank');
    }
  };
  
  const handleOrderSelect = (order) => {
    setSelectedOrder(order);
    fetchOrderDetails(order.order_id);
  };
  
  // Format date for readability
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  
  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" color="blue.500" />
        <Text mt={4}>Loading your orders...</Text>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <Text>{error}</Text>
      </Alert>
    );
  }
  
  if (orders.length === 0) {
    return (
      <Box py={8}>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <Heading size="sm">No orders found</Heading>
            <Text>You haven't placed any orders yet.</Text>
          </Box>
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box>
      <Heading size="lg" mb={6}>Your Orders</Heading>
      
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
        {/* Orders List */}
        <Box>
          <Heading size="md" mb={4}>Order History</Heading>
          <VStack spacing={4} align="stretch">
            {orders.map((order) => (
              <Box
                key={order.order_id}
                p={4}
                borderWidth="1px"
                borderRadius="md"
                borderColor={selectedOrder?.order_id === order.order_id ? 'blue.500' : borderColor}
                bg={selectedOrder?.order_id === order.order_id ? 'blue.50' : cardBg}
                cursor="pointer"
                onClick={() => handleOrderSelect(order)}
                _hover={{ borderColor: 'blue.300', shadow: 'sm' }}
                transition="all 0.2s"
              >
                <Flex justify="space-between" align="center">
                  <VStack align="start" spacing={1}>
                    <HStack>
                      <Text fontWeight="bold">Order #{order.order_id.substring(0, 8)}</Text>
                      <Badge colorScheme={order.status === 'paid' ? 'green' : 'blue'}>
                        {order.status}
                      </Badge>
                    </HStack>
                    <Text fontSize="sm" color="gray.500">
                      {formatDate(order.created_at)}
                    </Text>
                  </VStack>
                  <VStack align="end" spacing={1}>
                    <Text fontWeight="bold">${parseFloat(order.total).toFixed(2)}</Text>
                    <Text fontSize="sm" color="gray.600">{order.item_count} item(s)</Text>
                  </VStack>
                </Flex>
                <Divider my={2} />
                <Text fontSize="sm">{order.package} Package</Text>
              </Box>
            ))}
          </VStack>
        </Box>
        
        {/* Order Details */}
        <Box>
          {selectedOrder ? (
            detailsLoading ? (
              <Box textAlign="center" py={8}>
                <Spinner size="md" color="blue.500" />
                <Text mt={2}>Loading order details...</Text>
              </Box>
            ) : orderDetails ? (
              <Box>
                <Heading size="md" mb={4}>Order Details</Heading>
                <Box p={5} borderWidth="1px" borderRadius="md" bg={cardBg}>
                  <Flex justify="space-between" mb={4}>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" color="gray.500">Order ID</Text>
                      <Text fontWeight="medium">{orderDetails.order_data.id.substring(0, 8)}...</Text>
                    </VStack>
                    <VStack align="end" spacing={1}>
                      <Text fontSize="sm" color="gray.500">Date</Text>
                      <Text fontWeight="medium">{formatDate(orderDetails.order_data.created_at)}</Text>
                    </VStack>
                  </Flex>
                  
                  <Divider my={3} />
                  
                  <Box mb={4}>
                    <Text fontSize="sm" color="gray.500" mb={1}>Package</Text>
                    <Text fontWeight="medium">{orderDetails.order_data.package_name}</Text>
                  </Box>
                  
                  <Box mb={4}>
                    <Text fontSize="sm" color="gray.500" mb={1}>Payment Method</Text>
                    <Text fontWeight="medium">{orderDetails.order_data.payment_method}</Text>
                  </Box>
                  
                  {orderDetails.order_data.shipping_method && (
                    <Box mb={4}>
                      <Text fontSize="sm" color="gray.500" mb={1}>Shipping Method</Text>
                      <Text fontWeight="medium">{orderDetails.order_data.shipping_method}</Text>
                    </Box>
                  )}
                  
                  {orderDetails.order_data.shipping_address && (
                    <Accordion allowToggle mb={4}>
                      <AccordionItem border="none">
                        <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                          <Text fontSize="sm" color="gray.500" mb={1}>Shipping Address</Text>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel pb={4} px={0}>
                          <VStack align="start" spacing={1}>
                            <Text>{orderDetails.order_data.shipping_address.name}</Text>
                            <Text>{orderDetails.order_data.shipping_address.address}</Text>
                            <Text>
                              {orderDetails.order_data.shipping_address.city}, {orderDetails.order_data.shipping_address.state} {orderDetails.order_data.shipping_address.zip}
                            </Text>
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  )}
                  
                  <Divider my={3} />
                  
                  <Text fontWeight="medium" mb={2}>Order Items</Text>
                  <Table variant="simple" size="sm" mb={4}>
                    <Thead>
                      <Tr>
                        <Th>Item</Th>
                        <Th>Price</Th>
                        <Th>Action</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {orderDetails.items.map((item) => (
                        <Tr key={item.id}>
                          <Td>
                            <HStack>
                              {item.watermarked_url && (
                                <Image 
                                  src={item.watermarked_url} 
                                  alt={item.name}
                                  boxSize="40px"
                                  objectFit="cover"
                                  borderRadius="md"
                                  fallbackSrc="https://via.placeholder.com/40"
                                />
                              )}
                              <Text fontSize="sm">{item.name}</Text>
                            </HStack>
                          </Td>
                          <Td>${parseFloat(item.price).toFixed(2)}</Td>
                          <Td>
                            <Button
                              leftIcon={<DownloadIcon />}
                              colorScheme="blue"
                              size="xs"
                              onClick={() => handleDownload(item)}
                            >
                              Download
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  
                  <Divider my={3} />
                  
                  <Box>
                    <Flex justify="space-between" mb={1}>
                      <Text>Subtotal</Text>
                      <Text>${parseFloat(orderDetails.order_data.order_total).toFixed(2)}</Text>
                    </Flex>
                    
                    {orderDetails.order_data.shipping_cost > 0 && (
                      <Flex justify="space-between" mb={1}>
                        <Text>Shipping</Text>
                        <Text>${parseFloat(orderDetails.order_data.shipping_cost).toFixed(2)}</Text>
                      </Flex>
                    )}
                    
                    <Flex justify="space-between" fontWeight="bold" mt={2}>
                      <Text>Total</Text>
                      <Text>${(parseFloat(orderDetails.order_data.order_total) + parseFloat(orderDetails.order_data.shipping_cost || 0)).toFixed(2)}</Text>
                    </Flex>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Text>Could not retrieve order details</Text>
              </Alert>
            )
          ) : (
            <Box p={8} borderWidth="1px" borderRadius="md" bg={cardBg} textAlign="center">
              <Text color="gray.500">
                Select an order from the list to view details
              </Text>
            </Box>
          )}
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export default UserOrdersHistory; 